/**
 * PaymentsDownNotice — the "payments temporarily disabled" state.
 *
 * Reuses the same wording the backend returns from
 * /api/payments/status so the on-screen message and the toast/detail
 * that any checkout endpoint returns stay in sync. Includes a small
 * notify-me form that POSTs /api/payments/waitlist so we can email the
 * user once card checkout is live again.
 *
 * Consumers (PurchaseCreditsModal, Pricing, AccessExpiryBanner) either
 * (a) wrap their CTAs so the notice replaces the form when payments
 * are disabled, or (b) call the endpoint themselves and read the
 * `code === 'payments_disabled'` reply from a caught error.
 */
import React, { useContext, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { AlertTriangle, CheckCircle2, Mail } from 'lucide-react';

export default function PaymentsDownNotice({
  context = 'credits',
  message,
  compact = false,
  className = '',
}) {
  const { API, user } = useContext(AuthContext);
  const [email, setEmail] = useState(user?.email || '');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState('');

  const copy = message || (
    "Card payments are temporarily disabled while we finish a technical " +
    "troubleshooting session with our processor. Leave your email below " +
    "and we'll message you the moment checkout is live again."
  );

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Please enter a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/payments/waitlist`, { email, context, note });
      setQueued(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save your email. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 ${compact ? 'p-3' : 'p-4'} ${className}`}
      data-testid="payments-down-notice"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className={`text-amber-600 shrink-0 ${compact ? 'w-4 h-4 mt-0.5' : 'w-5 h-5 mt-0.5'}`} />
        <div className="flex-1 min-w-0">
          <div className={`font-semibold text-amber-900 dark:text-amber-100 ${compact ? 'text-sm' : ''}`}>
            Payments temporarily unavailable
          </div>
          <p className={`text-amber-800 dark:text-amber-200 mt-1 ${compact ? 'text-xs' : 'text-sm'}`}>
            {copy}
          </p>
          {queued ? (
            <div
              className="mt-3 flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-200"
              data-testid="payments-down-notice-success"
            >
              <CheckCircle2 className="w-4 h-4" />
              You're on the list. We'll email <strong>{email}</strong> as soon as checkout is live.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-3 flex flex-col sm:flex-row gap-2" data-testid="payments-down-notice-form">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 h-9 text-sm bg-white dark:bg-slate-900"
                data-testid="payments-down-notice-email"
                required
              />
              <Button
                type="submit"
                disabled={submitting}
                className="h-9 bg-amber-600 hover:bg-amber-700 text-white text-sm shrink-0"
                data-testid="payments-down-notice-submit"
              >
                <Mail className="w-3.5 h-3.5 mr-1.5" />
                {submitting ? 'Saving…' : 'Notify me'}
              </Button>
            </form>
          )}
          {error && (
            <p className="mt-1 text-xs text-red-600" data-testid="payments-down-notice-error">{error}</p>
          )}
          <p className={`text-amber-700 dark:text-amber-300 italic mt-2 ${compact ? 'text-[10px]' : 'text-xs'}`}>
            Existing subscriptions and access-code flows are unaffected — only new card charges are paused. Sorry for the inconvenience!
          </p>
        </div>
      </div>
    </div>
  );
}
